import { supabase } from '../lib/supabase';

/**
 * Audit Logging Service
 * Tracks all profile changes, access times, and data modifications
 * for compliance and data persistence verification
 */

class AuditLogService {
  /**
   * Log a profile update action
   * @param {string} userId - User ID
   * @param {Object} oldValues - Previous values
   * @param {Object} newValues - New values
   * @param {Array<string>} changedFields - List of changed field names
   * @param {Object} metadata - Additional metadata
   */
  async logProfileUpdate(userId, oldValues, newValues, changedFields, metadata = {}) {
    try {
      const { data, error } = await supabase
        ?.from('audit_logs')
        ?.insert({
          user_id: userId,
          action_type: 'profile_updated',
          action_description: `User profile updated: ${changedFields?.join(', ')}`,
          table_name: 'user_profiles',
          record_id: userId,
          old_values: oldValues,
          new_values: newValues,
          changed_fields: changedFields,
          ip_address: metadata?.ipAddress || null,
          user_agent: metadata?.userAgent || navigator?.userAgent,
          session_id: metadata?.sessionId || null,
          success: true,
          metadata: metadata
        })
        ?.select()
        ?.single();

      if (error) {
        console.error('Failed to log profile update:', error);
        return { success: false, error };
      }

      return { success: true, data };
    } catch (error) {
      console.error('Exception in logProfileUpdate:', error);
      return { success: false, error };
    }
  }

  /**
   * Log a profile view action
   * @param {string} userId - User ID
   * @param {Object} metadata - Additional metadata
   */
  async logProfileView(userId, metadata = {}) {
    try {
      const { data, error } = await supabase
        ?.from('audit_logs')
        ?.insert({
          user_id: userId,
          action_type: 'profile_viewed',
          action_description: 'User viewed their profile settings',
          table_name: 'user_profiles',
          record_id: userId,
          ip_address: metadata?.ipAddress || null,
          user_agent: metadata?.userAgent || navigator?.userAgent,
          session_id: metadata?.sessionId || null,
          success: true,
          metadata: metadata
        })
        ?.select()
        ?.single();

      if (error) {
        console.error('Failed to log profile view:', error);
        return { success: false, error };
      }

      return { success: true, data };
    } catch (error) {
      console.error('Exception in logProfileView:', error);
      return { success: false, error };
    }
  }

  /**
   * Log a login action
   * @param {string} userId - User ID
   * @param {Object} metadata - Additional metadata
   */
  async logLogin(userId, metadata = {}) {
    try {
      const { data, error } = await supabase
        ?.from('audit_logs')
        ?.insert({
          user_id: userId,
          action_type: 'login',
          action_description: 'User logged in successfully',
          ip_address: metadata?.ipAddress || null,
          user_agent: metadata?.userAgent || navigator?.userAgent,
          session_id: metadata?.sessionId || null,
          success: true,
          metadata: metadata
        })
        ?.select()
        ?.single();

      if (error) {
        console.error('Failed to log login:', error);
        return { success: false, error };
      }

      return { success: true, data };
    } catch (error) {
      console.error('Exception in logLogin:', error);
      return { success: false, error };
    }
  }

  /**
   * Log a logout action
   * @param {string} userId - User ID
   * @param {Object} metadata - Additional metadata
   */
  async logLogout(userId, metadata = {}) {
    try {
      const { data, error } = await supabase
        ?.from('audit_logs')
        ?.insert({
          user_id: userId,
          action_type: 'logout',
          action_description: 'User logged out',
          ip_address: metadata?.ipAddress || null,
          user_agent: metadata?.userAgent || navigator?.userAgent,
          session_id: metadata?.sessionId || null,
          success: true,
          metadata: metadata
        })
        ?.select()
        ?.single();

      if (error) {
        console.error('Failed to log logout:', error);
        return { success: false, error };
      }

      return { success: true, data };
    } catch (error) {
      console.error('Exception in logLogout:', error);
      return { success: false, error };
    }
  }

  /**
   * Log a failed action
   * @param {string} userId - User ID
   * @param {string} actionType - Type of action
   * @param {string} errorMessage - Error message
   * @param {Object} metadata - Additional metadata
   */
  async logFailedAction(userId, actionType, errorMessage, metadata = {}) {
    try {
      const { data, error } = await supabase
        ?.from('audit_logs')
        ?.insert({
          user_id: userId,
          action_type: actionType,
          action_description: `Failed action: ${actionType}`,
          ip_address: metadata?.ipAddress || null,
          user_agent: metadata?.userAgent || navigator?.userAgent,
          session_id: metadata?.sessionId || null,
          success: false,
          error_message: errorMessage,
          metadata: metadata
        })
        ?.select()
        ?.single();

      if (error) {
        console.error('Failed to log failed action:', error);
        return { success: false, error };
      }

      return { success: true, data };
    } catch (error) {
      console.error('Exception in logFailedAction:', error);
      return { success: false, error };
    }
  }

  /**
   * Get audit logs for a user
   * @param {string} userId - User ID
   * @param {Object} options - Query options
   * @returns {Promise<Object>} Audit logs
   */
  async getUserAuditLogs(userId, options = {}) {
    try {
      const {
        limit = 50,
        offset = 0,
        actionType = null,
        startDate = null,
        endDate = null,
        orderBy = 'created_at',
        orderDirection = 'desc'
      } = options;

      let query = supabase
        ?.from('audit_logs')
        ?.select('*', { count: 'exact' })
        ?.eq('user_id', userId);

      // Filter by action type
      if (actionType) {
        query = query?.eq('action_type', actionType);
      }

      // Filter by date range
      if (startDate) {
        query = query?.gte('created_at', startDate);
      }
      if (endDate) {
        query = query?.lte('created_at', endDate);
      }

      // Order and pagination
      query = query
        ?.order(orderBy, { ascending: orderDirection === 'asc' })
        ?.range(offset, offset + limit - 1);

      const { data, error, count } = await query;

      if (error) {
        console.error('Failed to fetch audit logs:', error);
        return { success: false, error };
      }

      return {
        success: true,
        data,
        count,
        hasMore: count > offset + limit
      };
    } catch (error) {
      console.error('Exception in getUserAuditLogs:', error);
      return { success: false, error };
    }
  }

  /**
   * Get audit log statistics for a user
   * @param {string} userId - User ID
   * @returns {Promise<Object>} Statistics
   */
  async getAuditStatistics(userId) {
    try {
      const { data, error } = await supabase
        ?.from('audit_logs')
        ?.select('action_type, success')
        ?.eq('user_id', userId);

      if (error) {
        console.error('Failed to fetch audit statistics:', error);
        return { success: false, error };
      }

      // Calculate statistics
      const stats = {
        total: data?.length || 0,
        successful: data?.filter(log => log?.success)?.length || 0,
        failed: data?.filter(log => !log?.success)?.length || 0,
        byActionType: {}
      };

      // Count by action type
      data?.forEach(log => {
        if (!stats?.byActionType?.[log?.action_type]) {
          stats.byActionType[log?.action_type] = 0;
        }
        stats.byActionType[log?.action_type]++;
      });

      return { success: true, data: stats };
    } catch (error) {
      console.error('Exception in getAuditStatistics:', error);
      return { success: false, error };
    }
  }

  /**
   * Compare field values to detect changes
   * @param {Object} oldValues - Previous values
   * @param {Object} newValues - New values
   * @returns {Object} Changed fields and their values
   */
  detectChanges(oldValues, newValues) {
    const changes = {
      changedFields: [],
      oldValues: {},
      newValues: {}
    };

    Object.keys(newValues)?.forEach(key => {
      if (oldValues?.[key] !== newValues?.[key]) {
        changes?.changedFields?.push(key);
        changes.oldValues[key] = oldValues?.[key];
        changes.newValues[key] = newValues?.[key];
      }
    });

    return changes;
  }
}

export default new AuditLogService();